// Import Monaco configuration
import "../../monacoConfig";
import {
  UnstyledOpenInCodeSandboxButton,
  SandpackProvider,
  SandboxEnvironment,
  SandpackThemeProvider,
  SandpackCodeEditor,
  // SandpackPreview,
  // SandpackLayout,
  // useSandpack,
} from "@codesandbox/sandpack-react";
import { nightOwl, aquaBlue } from "@codesandbox/sandpack-themes";
import { RotateCw, Fullscreen, ExternalLink } from "lucide-react";
//import { useNavigate } from "@tanstack/react-router";
import { useDarkMode, useEnrollment } from "@/lib/store";
import WebContainerPreview from "./WebContainerPreview";
import { MonacoEditor } from "./MonacoEditor";

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { Heights, Widths } from "@/lib/sizes";
import { Button } from "@/components/ui/button";
import { PlaygroundFileExplorer } from "./PlaygroundFileExplorer";
import { TooltipButton } from "@/components/format/Buttons";

export interface SandpackFiles {
  activeFile: string;
  visibleFiles: string[];
  files: Record<string, any>;
}

export interface PlaygroundProps {
  elementId?: string; //not passed in CodePLaygroundType
  sectionId?: string; //not passed in CodePLaygroundType
  enrollmentId?: string; //not passed in CodePLaygroundType
  baseFiles: Record<string, any>;
  updateParentForm?: (
    files: SandpackFiles,
    environment: SandboxEnvironment,
  ) => void | undefined;
  canEditFiles?: boolean;
  showFileExplorer?: boolean;
  showFileTabs?: boolean;
  showEditor?: boolean;
  showPreview?: boolean;
  showTerminal?: boolean;
  showOpenInCodeSandbox?: boolean;
  width?: keyof typeof Widths;
  height?: keyof typeof Heights;
  splitPercent?: number;
  readOnly?: boolean;
  mountPoint: string;
  disabled?: boolean;
  environment?: SandboxEnvironment;
  direction?: "horizontal" | "vertical";
  highlightText?: string;
  highlightRegex?: string;
  highlightLines?: string;
  // fullScreen?: boolean;
  setFullScreen?: (fullScreen: boolean) => void;
}

export function Playground(props: PlaygroundProps) {
  const baseFiles = props.baseFiles;
  const [defaultFiles, setDefaultFiles] = useState(baseFiles.files);

  useEffect(() => {
    //console.log("set default files called in Playground.tsx"); //not fs problem
    setDefaultFiles(baseFiles.files); //for resetting!
  }, [baseFiles]);

  const { activeFile, visibleFiles } = props.baseFiles;
  // experiemntal bundler cant do static
  // bundlerURL: "https://bundler.up.railway.app",
  //bundlerURL:"https://preview.sandpack-static-server.codesandbox.io",
  //bundlerURL: "https://sandpack-bundler.codesandbox.io"//https://github.com/codesandbox/sandpack/blob/8716a577694885b53e0696a275ecbf0b87097fc1/sandpack-react/src/types.ts#L120
  // autorun: true,
  //autoReload: true,
  return (
    <SandpackProvider
      customSetup={{
        environment: props.environment ? props.environment : "static",
      }}
      options={{
        autorun: false,
        autoReload: false,
        activeFile,
        visibleFiles,
        initMode: "immediate",
      }}
      files={defaultFiles}
    >
      <PlaygroundPanels {...props} splitPercent={props.splitPercent} />
    </SandpackProvider>
  );
}

//extracted so we can use use Sandpack hooks
function PlaygroundPanels({
  sectionId,
  elementId,
  enrollmentId,
  baseFiles, // populated by
  updateParentForm, //needed by outside form to extract data
  mountPoint,
  canEditFiles = false,
  showFileExplorer = false,
  showFileTabs = true, //cant think of when we dont want to show tabs
  showEditor = true,
  showPreview = true,
  showTerminal = false,
  showOpenInCodeSandbox = true,
  splitPercent = 50,
  height = "md",
  width = "full",
  disabled = false, // when translating
  readOnly = false,
  //environment = "static",
  direction = "horizontal",
  highlightText = "",
  highlightRegex = "",
  highlightLines = "",
  // fullScreen = false,
  setFullScreen,
}: PlaygroundProps) {
  const isDark = useDarkMode();
  //const { updateFile, setActiveFile, resetAllFiles } = sandpack;
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewId, setPreviewId] = useState(1);
  const editorDisabled = disabled;

  if (!showPreview && !showEditor) return;
  const [fs, setFs] = useState(false);
  const style = useMemo(
    () => ({
      height: fs ? "100dvh" : Heights[height],
    }),
    [height, fs],
  );

  return (
    <SandpackThemeProvider theme={isDark ? nightOwl : aquaBlue}>
      <div
        className={cn(
          "bg-background rounded-md",
          fs ? "fixed inset-0 z-10 " : "relative",
          fs && showEditor ? "border-b-64" : "",
        )}
        style={style}
      >
        <ResizablePanelGroup
          direction={direction}
          className={cn(
            "rounded-md md:min-w-[450px] relative",
            Widths[fs ? "full" : width],
          )}
        >
          {showEditor && (
            <ResizablePanel defaultSize={splitPercent * 1.0}>
              <div
                key={mountPoint /* reloads the playground */}
                className={cn(
                  "flex flex-row-reverse h-full relative",
                  editorDisabled ? "opacity-50" : "opacity-100",
                )}
              >
                <MonacoEditor
                  baseFiles={baseFiles}
                  showFileTabs={showFileTabs}
                  closableTabs={showFileExplorer}
                  readOnly={editorDisabled || readOnly}
                  updateParentForm={updateParentForm}
                  highlightText={highlightText}
                  highlightRegex={highlightRegex}
                  highlightLines={highlightLines}
                />
                {/* <SandpackCodeEditor/> */}

                {showFileExplorer && (
                  <div
                    className={canEditFiles ? "flex h-full" : "absolute left-0"}
                  >
                    <PlaygroundFileExplorer canEditFiles={canEditFiles} />
                  </div>
                )}
              </div>
            </ResizablePanel>
          )}
          {showEditor && showPreview && <ResizableHandle withHandle />}
          {showPreview && (
            <ResizablePanel defaultSize={100.0 - splitPercent}>
              <div
                className={cn(
                  "flex h-full justify-center w-full",
                  direction == "horizontal" ? "border-s-2" : "border-t-4",
                )}
              >
                {/* <StaticPreview /> */}
                {/* <SandpackLayout>
                <SandpackPreview
                  showOpenInCodeSandbox={showOpenInCodeSandbox}
                />
              </SandpackLayout> */}
                <WebContainerPreview
                  baseFiles={baseFiles}
                  setPreviewLoading={setPreviewLoading}
                  mountPoint={mountPoint}
                  showTerminal={showTerminal}
                  previewId={previewId}
                />
              </div>
            </ResizablePanel>
          )}

          <div className="flex gap-2 absolute bottom-1 right-20 text-muted-foreground opacity-50">
            {showOpenInCodeSandbox && <OpenInCodeSandboxButton />}

            {showPreview && (
              <>
                <TooltipButton
                  info="Refresh preview"
                  variant="secondary"
                  className="rounded-full h-7 px-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setPreviewId(previewId + 1);
                  }}
                >
                  <RotateCw />
                </TooltipButton>

                {elementId && sectionId && (
                  <OpenPreviewInNewTabButton
                    elementId={elementId}
                    sectionId={sectionId}
                    enrollmentId={enrollmentId}
                  />
                )}
              </>
            )}

            <TooltipButton
              info="Toggle fullscreen"
              variant="secondary"
              className="rounded-full h-7 px-2"
              onClick={(e) => {
                e.preventDefault();
                setFs(!fs);
                if (setFullScreen) setFullScreen(!fs);
              }}
            >
              <Fullscreen />
            </TooltipButton>
          </div>
        </ResizablePanelGroup>
      </div>
    </SandpackThemeProvider>
  );
}

function OpenPreviewInNewTabButton({
  elementId,
  sectionId,
  enrollmentId,
}: {
  elementId: string;
  sectionId: string;
  enrollmentId?: string;
}) {
  const enrollment = useEnrollment();
  if (!enrollment && !enrollmentId) return;
  if (!elementId || !sectionId) return;
  return (
    <TooltipButton
      info="Open in new tab"
      variant="secondary"
      className="rounded-full h-7 px-2"
      onClick={(e) => {
        e.preventDefault();
        window.open(
          `/playground?elementId=${elementId}&sectionId=${sectionId}&enrollmentId=${enrollmentId ? enrollmentId : enrollment.id}`,
          "_blank",
        );
      }}
    >
      <ExternalLink />
    </TooltipButton>
  );
}

function OpenInCodeSandboxButton() {
  return (
    <UnstyledOpenInCodeSandboxButton className="rounded-full border-2 border-muted-foreground h-6 px-2 text-xs bg-secondary text-secondary-foreground font-medium">
      Open in CodeSandbox
    </UnstyledOpenInCodeSandboxButton>
  );
}
